/**
 * Géoplan — logique pure (Boîte à outils, 6e → 4e).
 *
 * Polygones dont les sommets sont des clous (points entiers) : aire EXACTE
 * (formule du lacet : un multiple de 0,5), longueur des côtés (entière s'il
 * est horizontal ou vertical, √(a² + b²) sinon), carreaux entiers et
 * entamés. Figures à formule : rectangle, triangle, parallélogramme, disque.
 */

export interface Pt {
  x: number;
  y: number;
}

export const N = 8; // clous de 0 à 8

/** deux fois l'aire (entier), signe = sens de parcours */
export function twiceArea(pts: readonly Pt[]): number {
  let s = 0;
  pts.forEach((p, i) => {
    const q = pts[(i + 1) % pts.length]!;
    s += p.x * q.y - q.x * p.y;
  });
  return s;
}

export const area = (pts: readonly Pt[]) => Math.abs(twiceArea(pts)) / 2;

export interface Side {
  dx: number;
  dy: number;
  /** longueur exacte : « 3 », « \sqrt{5} », « 2\sqrt{2} » */
  tex: string;
  plain: string;
  value: number;
  straight: boolean;
}

export function side(p: Pt, q: Pt): Side {
  const dx = Math.abs(q.x - p.x);
  const dy = Math.abs(q.y - p.y);
  const value = Math.hypot(dx, dy);
  if (dx === 0 || dy === 0) {
    const n = dx + dy;
    return { dx, dy, tex: String(n), plain: String(n), value, straight: true };
  }
  // collège : √(a² + b²) sans simplifier (√8, pas 2√2 : c'est du programme de 2nde)
  const n2 = dx * dx + dy * dy;
  const r = Math.round(Math.sqrt(n2));
  const perfect = r * r === n2;
  const tex = perfect ? String(r) : `\\sqrt{${n2}}`;
  const plain = perfect ? String(r) : `√${n2}`;
  return { dx, dy, tex, plain, value, straight: perfect };
}

export const sides = (pts: readonly Pt[]) => pts.map((p, i) => side(p, pts[(i + 1) % pts.length]!));

/** valeur approchée au centième, deux décimales écrites (« 8,60 ») */
export const fmt2 = (v: number) => v.toFixed(2).replace('.', ',');

/** un nombre à la française, au plus `d` décimales */
export const fmt = (v: number, d = 2) => {
  const r = Number(v.toFixed(d));
  return String(Object.is(r, -0) ? 0 : r).replace('.', ',');
};

/** périmètre : exact s'il n'y a que des côtés entiers, sinon approché */
export function perimeter(pts: readonly Pt[]): { exact: boolean; value: number; text: string } {
  const ss = sides(pts);
  const exact = ss.every((x) => x.straight);
  // approché : la somme des côtés ARRONDIS au centième (ce que l'élève additionne)
  const value = exact
    ? ss.reduce((s, x) => s + x.value, 0)
    : ss.reduce((s, x) => s + Math.round(x.value * 100), 0) / 100;
  return { exact, value, text: exact ? String(Math.round(value)) : `≈ ${fmt2(value)}` };
}

/** point dans le polygone (rayon horizontal), bord exclu */
export function inside(p: Pt, pts: readonly Pt[]): boolean {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) c = !c;
  }
  return c;
}

export type CellKind = 'full' | 'part' | 'out';

/** aire de la partie d'un polygone (simple) dans le carreau [i ; i+1] × [j ; j+1] (Sutherland–Hodgman) */
export function cellArea(pts: readonly Pt[], i: number, j: number): number {
  const clip = (poly: Pt[], inside: (p: Pt) => boolean, cut: (a: Pt, b: Pt) => Pt): Pt[] => {
    const out: Pt[] = [];
    poly.forEach((cur, k) => {
      const prev = poly[(k + poly.length - 1) % poly.length]!;
      const ci = inside(cur);
      const pi = inside(prev);
      if (ci) {
        if (!pi) out.push(cut(prev, cur));
        out.push(cur);
      } else if (pi) out.push(cut(prev, cur));
    });
    return out;
  };
  const atX = (x: number) => (a: Pt, b: Pt) => ({
    x,
    y: a.y + ((b.y - a.y) * (x - a.x)) / (b.x - a.x),
  });
  const atY = (y: number) => (a: Pt, b: Pt) => ({
    x: a.x + ((b.x - a.x) * (y - a.y)) / (b.y - a.y),
    y,
  });
  let poly = [...pts];
  poly = clip(poly, (p) => p.x >= i, atX(i));
  if (poly.length) poly = clip(poly, (p) => p.x <= i + 1, atX(i + 1));
  if (poly.length) poly = clip(poly, (p) => p.y >= j, atY(j));
  if (poly.length) poly = clip(poly, (p) => p.y <= j + 1, atY(j + 1));
  return poly.length < 3 ? 0 : area(poly);
}

/** carreaux d'un polygone : entiers, entamés ou dehors (calcul EXACT des aires) */
export function polygonCells(pts: readonly Pt[]): Map<string, CellKind> {
  const out = new Map<string, CellKind>();
  for (let i = 0; i < N; i++)
    for (let j = 0; j < N; j++) {
      const a = cellArea(pts, i, j);
      out.set(`${i},${j}`, a > 1 - 1e-9 ? 'full' : a < 1e-9 ? 'out' : 'part');
    }
  return out;
}

/**
 * Carreaux d'une figure courbe (disque) : échantillonnage fin, jamais sur un
 * bord entier.
 */
export function cells(test: (p: Pt) => boolean): Map<string, CellKind> {
  const out = new Map<string, CellKind>();
  const S = 12;
  for (let i = 0; i < N; i++)
    for (let j = 0; j < N; j++) {
      let n = 0;
      for (let a = 0; a < S; a++)
        for (let b = 0; b < S; b++) if (test({ x: i + (a + 0.5) / S, y: j + (b + 0.5) / S })) n++;
      out.set(`${i},${j}`, n === S * S ? 'full' : n === 0 ? 'out' : 'part');
    }
  return out;
}

export const countCells = (m: Map<string, CellKind>) => {
  let full = 0;
  let part = 0;
  for (const v of m.values()) {
    if (v === 'full') full++;
    else if (v === 'part') part++;
  }
  return { full, part };
};

/* polygone simple : deux côtés non voisins ne se coupent pas */
/** deux segments se coupent ou se touchent */
export function crosses(a: Pt, b: Pt, c: Pt, d: Pt): boolean {
  const o = (p: Pt, q: Pt, r: Pt) =>
    Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
  const onSeg = (p: Pt, q: Pt, r: Pt) =>
    Math.min(p.x, q.x) <= r.x &&
    r.x <= Math.max(p.x, q.x) &&
    Math.min(p.y, q.y) <= r.y &&
    r.y <= Math.max(p.y, q.y);
  const o1 = o(a, b, c);
  const o2 = o(a, b, d);
  const o3 = o(c, d, a);
  const o4 = o(c, d, b);
  if (o1 !== o2 && o3 !== o4 && o1 * o2 <= 0 && o3 * o4 <= 0) return true;
  // contacts et chevauchements alignés
  if (o1 === 0 && onSeg(a, b, c)) return true;
  if (o2 === 0 && onSeg(a, b, d)) return true;
  if (o3 === 0 && onSeg(c, d, a)) return true;
  if (o4 === 0 && onSeg(c, d, b)) return true;
  return false;
}

export function isSimple(pts: readonly Pt[]): boolean {
  const n = pts.length;
  if (n < 3) return false;
  if (twiceArea(pts) === 0) return false;
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (crosses(pts[i]!, pts[(i + 1) % n]!, pts[j]!, pts[(j + 1) % n]!)) return false;
    }
  return true;
}

/** rectangles à côtés entiers de même aire (« même aire, périmètres différents ») */
export function sameAreaRectangles(a: number): Array<{ w: number; h: number; p: number }> {
  const out: Array<{ w: number; h: number; p: number }> = [];
  for (let w = 1; w * w <= a; w++) if (a % w === 0) out.push({ w, h: a / w, p: 2 * (w + a / w) });
  return out;
}

/** la ligne brisée (pas encore fermée) ne se recoupe pas : on teste son dernier côté */
export function openIsSimple(pts: readonly Pt[]): boolean {
  const n = pts.length;
  if (n < 3) return true;
  const c = pts[n - 2]!;
  const d = pts[n - 1]!;
  const o = (p: Pt, q: Pt, r: Pt) =>
    Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x));
  const on = (p: Pt, q: Pt, r: Pt) =>
    Math.min(p.x, q.x) <= r.x &&
    r.x <= Math.max(p.x, q.x) &&
    Math.min(p.y, q.y) <= r.y &&
    r.y <= Math.max(p.y, q.y);
  for (let i = 0; i < n - 2; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    if (i === n - 3) {
      // côté voisin (sommet commun c) : interdit de revenir sur ses pas
      if (o(a, b, d) === 0 && (on(a, b, d) || on(c, d, a))) return false;
      continue;
    }
    if (crosses(a, b, c, d)) return false;
  }
  return true;
}
